'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CartProduct as Product } from '@/types/product';
import { UserProfile } from '../lib/user-profile-types';
import { PackageWithImage } from '@/lib/package-utils';

// Define the quotation item type with complete package data
export type QuotationItem = {
  product: Product;
  quantity: number;
  packageId?: number; // Optional package ID for package-based products
  packageData?: PackageWithImage; // Complete package information
  notes?: string;
};

// Quotation form field types
export interface QuotationFormData {
  name: string;
  email: string;
  phone: string;
  company?: string;
  message: string;
  quantity: number;
}

// Default form values
const defaultFormData: QuotationFormData = {
  name: '',
  email: '',
  phone: '',
  company: '',
  message: '',
  quantity: 1,
};

// Define the context type
type QuotationContextType = {
  items: QuotationItem[];
  addItem: (product: Product, quantity: number, packageData?: PackageWithImage) => void;
  removeItem: (productId: number, packageId?: number) => void;
  updateItemQuantity: (productId: number, quantity: number, packageId?: number) => void;
  updateItemNotes: (productId: number, notes: string, packageId?: number) => void;
  clearItems: () => void;
  itemCount: number;
  isInQuotation: (productId: number, packageId?: number) => boolean;
  formData: QuotationFormData;
  updateFormData: (data: Partial<QuotationFormData>) => void;
  resetForm: () => void;
  selectedProduct: Product | null;
  selectProduct: (product: Product) => void;
  isSubmitting: boolean;
  submitQuotation: () => Promise<boolean>;
  showConfirmation: boolean;
  setShowConfirmation: (show: boolean) => void;
  quoteNumber: string | null;
  setQuoteNumber: (quoteNumber: string | null) => void;
  formErrors: Record<string, string>;
  validateForm: () => boolean;
  userProfile: UserProfile | null;
  isLoadingProfile: boolean;
};

// Create the context with default values
const QuotationContext = createContext<QuotationContextType>({
  items: [],
  addItem: () => {},
  removeItem: () => {},
  updateItemQuantity: () => {},
  updateItemNotes: () => {},
  clearItems: () => {},
  itemCount: 0,
  isInQuotation: () => false,
  formData: defaultFormData,
  updateFormData: () => {},
  resetForm: () => {},
  selectedProduct: null,
  selectProduct: () => {},
  isSubmitting: false,
  submitQuotation: async () => false,
  showConfirmation: false,
  setShowConfirmation: () => {},
  quoteNumber: null,
  setQuoteNumber: () => {},
  formErrors: {},
  validateForm: () => false,
  userProfile: null,
  isLoadingProfile: true,
});

// Local storage key
const STORAGE_KEY = 'quotation_items';

// Provider component
export function QuotationProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<QuotationItem[]>([]);
  const [formData, setFormData] = useState<QuotationFormData>(defaultFormData);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState<string | null>(null);

  // Load items from localStorage on initial render
  useEffect(() => {
    try {
      const savedItems = localStorage.getItem(STORAGE_KEY);
      if (savedItems) {
        const parsedItems: QuotationItem[] = JSON.parse(savedItems);
        console.log('[QuotationContext] Loaded quotation items from localStorage:', parsedItems);
        setItems(parsedItems);
      }
    } catch (error) {
      console.error('Error loading quotation items from localStorage:', error);
    }
  }, []);

  // Save items to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Error saving quotation items to localStorage:', error);
    }
  }, [items]);

  // Load user profile data
  useEffect(() => {
    async function loadUserProfile() {
      setIsLoadingProfile(true);
      try {
        // Make a fetch request instead of directly importing server functions
        const response = await fetch('/api/user/profile');
        if (response.ok) {
          const profile = await response.json();
          setUserProfile(profile);
          
          // Pre-fill form with user profile data if available and form is empty
          if (profile && !formData.name) {
            setFormData(prevData => ({
              ...prevData,
              name: profile.fullName || '',
              email: profile.email || '',
              phone: profile.phoneNumber || '',
              company: profile.company || ''
            }));
          }
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    }
    
    loadUserProfile();
  }, []);

  // Add an item to the quotation
  const addItem = (product: Product, quantity: number, packageData?: PackageWithImage) => {
    const packageId = packageData?.id;
    console.log('[QuotationContext] ===== ADDING ITEM TO QUOTATION =====');
    console.log('[QuotationContext] Product:', product.name);
    console.log('[QuotationContext] Quantity:', quantity);
    console.log('[QuotationContext] PackageData provided?:', !!packageData);
    console.log('[QuotationContext] PackageId:', packageId);
    if (packageData) {
      console.log('[QuotationContext] Package details:', {
        id: packageData.id,
        packageType: packageData.packageType,
        weight: packageData.weight,
        unit: packageData.unit,
        price_ars: packageData.price_ars
      });
    } else {
      console.log('[QuotationContext] NO PACKAGE DATA PROVIDED!');
    }
    setItems(prevItems => {
      console.log('[QuotationContext] Current items before adding:', prevItems.map(item => ({
        productId: item.product.id,
        productName: item.product.name,
        packageId: item.packageId,
        quantity: item.quantity
      })));
      
      // For packages, match by both product ID and package ID
      const existingItemIndex = prevItems.findIndex(item => 
        item.product.id === product.id && item.packageId === packageId
      );
      
      if (existingItemIndex >= 0) {
        console.log('[QuotationContext] Found existing item at index:', existingItemIndex);
        // Update existing item
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + quantity,
          // Update package data if provided
          ...(packageData && { packageData })
        };
        return updatedItems;
      } else {
        console.log('[QuotationContext] Adding new item');
        // Add new item
        const newItems = [...prevItems, { product, quantity, packageId, packageData }];
        console.log('[QuotationContext] New items after adding:', newItems.map(item => ({
          productId: item.product.id,
          productName: item.product.name,
          packageId: item.packageId,
          quantity: item.quantity
        })));
        return newItems;
      }
    });
  };

  // Remove an item from the quotation
  const removeItem = (productId: number, packageId?: number) => {
    setItems(prevItems => prevItems.filter(item => 
      !(item.product.id === productId && item.packageId === packageId)
    ));
  };

  // Update item quantity
  const updateItemQuantity = (productId: number, quantity: number, packageId?: number) => {
    if (quantity <= 0) {
      removeItem(productId, packageId);
      return;
    }
    
    setItems(prevItems => 
      prevItems.map(item => 
        item.product.id === productId && item.packageId === packageId
          ? { ...item, quantity } 
          : item
      )
    );
  };

  // Update item notes
  const updateItemNotes = (productId: number, notes: string, packageId?: number) => {
    setItems(prevItems => 
      prevItems.map(item => 
        item.product.id === productId && item.packageId === packageId
          ? { ...item, notes } 
          : item
      )
    );
  };

  // Clear all items
  const clearItems = () => {
    setItems([]);
  };

  // Get total number of items
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);

  // Check if a product is already in the quotation
  const isInQuotation = (productId: number, packageId?: number) => {
    return items.some(item => 
      item.product.id === productId && item.packageId === packageId
    );
  };

  // Update form data
  const updateFormData = (data: Partial<QuotationFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  // Reset form
  const resetForm = () => {
    setFormData(defaultFormData);
    setSelectedProduct(null);
    setFormErrors({});
  };

  // Select product for quotation
  const selectProduct = (product: Product) => {
    setSelectedProduct(product);
  };

  // Validate form fields
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) errors.name = 'El nombre es requerido';
    if (!formData.email.trim()) errors.email = 'El correo electrónico es requerido';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) 
      errors.email = 'Ingrese un correo electrónico válido';
    
    if (!formData.phone.trim()) errors.phone = 'El teléfono es requerido';
    if (!formData.message.trim()) errors.message = 'El mensaje es requerido';
    if (formData.quantity < 1) errors.quantity = 'La cantidad debe ser al menos 1';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit quotation request
  const submitQuotation = async (): Promise<boolean> => {
    if (!validateForm()) return false;
    if (items.length === 0) return false;
    
    setIsSubmitting(true);
    
    try {
      const quotationData = {
        items: items.map(item => ({
          product: {
            id: item.product.id,
            name: item.product.name,
            category: item.product.category
          },
          quantity: item.quantity,
          packageId: item.packageId, // Include package information
          notes: item.notes || ''
        })),
        contactDetails: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company || undefined
        },
        specialRequirements: formData.message
      };

      const response = await fetch('/api/quotations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quotationData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al enviar la cotización');
      }
      
      const result = await response.json();
      
      // Set quote number and show confirmation modal
      setQuoteNumber(result.quoteNumber);
      setShowConfirmation(true);
      
      // Reset form after successful submission
      resetForm();
      return true;
    } catch (error) {
      console.error('Error submitting quotation:', error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <QuotationContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateItemQuantity,
        updateItemNotes,
        clearItems,
        itemCount,
        isInQuotation,
        formData,
        updateFormData,
        resetForm,
        selectedProduct,
        selectProduct,
        isSubmitting,
        submitQuotation,
        showConfirmation,
        setShowConfirmation,
        quoteNumber,
        setQuoteNumber,
        formErrors,
        validateForm,
        userProfile,
        isLoadingProfile
      }}
    >
      {children}
    </QuotationContext.Provider>
  );
}

// Custom hook to use the context
export function useQuotation() {
  const context = useContext(QuotationContext);
  if (context === undefined) {
    throw new Error('useQuotation must be used within a QuotationProvider');
  }
  return context;
} 